<script setup lang="ts">
import type { SizeChart } from '~~/shared/types/store'
import { findSizeColumn } from '~~/shared/utils/sizeChartLookup'

const props = defineProps<{
  sizeChart: SizeChart | null
  selectedSize?: string
}>()

const isOpen = ref(false)

const hasStructuredData = computed(
  () => !!props.sizeChart && props.sizeChart.headers.length > 0,
)

const lookup = computed(() => {
  if (!props.sizeChart || !props.selectedSize) return null
  return findSizeColumn(
    props.sizeChart.headers,
    props.sizeChart.rows,
    props.selectedSize,
  )
})

const headerCells = computed(() => {
  if (!props.sizeChart) return []
  const headers = props.sizeChart.headers
  if (headers.length === 0) return []
  return headers
})

const dataRows = computed(() => props.sizeChart?.rows ?? [])

function isSelectedHeaderCell(headerIdx: number) {
  if (!lookup.value) return false
  if (lookup.value.sizeRowIndex !== -1) return false
  return headerIdx - 1 === lookup.value.columnIndex
}

function isSelectedValueCell(rowIdx: number, valueIdx: number) {
  if (!lookup.value) return false
  if (lookup.value.sizeRowIndex !== -1 && lookup.value.sizeRowIndex !== rowIdx) {
    return valueIdx === lookup.value.columnIndex
  }
  return valueIdx === lookup.value.columnIndex
}

function isSelectedRowLabel(rowIdx: number) {
  if (!lookup.value) return false
  return lookup.value.sizeRowIndex === rowIdx
}

function close() {
  isOpen.value = false
}

watch(isOpen, value => {
  if (typeof document === 'undefined') return
  document.body.style.overflow = value ? 'hidden' : ''
})

onBeforeUnmount(() => {
  if (typeof document !== 'undefined') document.body.style.overflow = ''
})

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') close()
}
</script>

<template>
  <template v-if="sizeChart">
    <button type="button" class="size-chart-trigger" @click="isOpen = true">
      <svg
        class="size-chart-trigger__icon"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="6" width="18" height="12" rx="1.5" />
        <path d="M7 6v3M11 6v4M15 6v3M19 6v4" />
      </svg>
      <span>Tabela rozmiarów</span>
    </button>

    <Teleport to="body">
      <Transition name="size-chart-modal">
        <div
          v-if="isOpen"
          class="size-chart-backdrop"
          role="presentation"
          tabindex="-1"
          @click.self="close"
          @keydown="onKeydown"
        >
          <div
            class="size-chart-dialog"
            role="dialog"
            aria-modal="true"
            :aria-label="sizeChart.title"
          >
            <header class="size-chart-dialog__header">
              <div>
                <p class="size-chart-dialog__eyebrow">Tabela rozmiarów</p>
                <h2 class="size-chart-dialog__title">{{ sizeChart.title }}</h2>
                <p v-if="selectedSize" class="size-chart-dialog__hint">
                  Twój rozmiar:
                  <span class="size-chart-dialog__hint-value">{{ selectedSize }}</span>
                </p>
              </div>
              <button
                type="button"
                class="size-chart-dialog__close"
                aria-label="Zamknij"
                @click="close"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                >
                  <path d="M6 6l12 12M6 18L18 6" />
                </svg>
              </button>
            </header>

            <div class="size-chart-dialog__body">
              <template v-if="hasStructuredData">
                <div class="size-chart-table-wrap">
                  <table class="size-chart-table">
                    <thead>
                      <tr>
                        <th
                          v-for="(cell, idx) in headerCells"
                          :key="`h-${idx}`"
                          :class="{
                            'size-chart-table__th--label': idx === 0,
                            'size-chart-table__th--selected': isSelectedHeaderCell(idx),
                          }"
                          scope="col"
                        >
                          {{ cell }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, rowIdx) in dataRows"
                        :key="`r-${rowIdx}`"
                        :class="{ 'size-chart-table__tr--size-row': isSelectedRowLabel(rowIdx) }"
                      >
                        <th scope="row" class="size-chart-table__label">{{ row.label }}</th>
                        <td
                          v-for="(value, valueIdx) in row.values"
                          :key="`v-${rowIdx}-${valueIdx}`"
                          :class="{
                            'size-chart-table__cell--selected': isSelectedValueCell(rowIdx, valueIdx),
                          }"
                        >
                          {{ value || '–' }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p class="size-chart-dialog__note">
                  Pomiary podane w&nbsp;centymetrach. Rozmiar najlepiej dopasujesz porównując własne wymiary ciała z&nbsp;tabelą.
                </p>
              </template>

              <div v-else class="size-chart-html" v-html="sizeChart.html" />
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </template>
</template>

<style scoped>
.size-chart-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 0;
  padding: 4px 0;
  margin-top: 6px;
  font-size: 0.875rem;
  color: var(--color-text-secondary, #5a5a5a);
  cursor: pointer;
  font-family: inherit;
  transition: color var(--transition-fast, 150ms ease);
}

.size-chart-trigger:hover,
.size-chart-trigger:focus-visible {
  color: var(--color-text-primary, #151515);
}

.size-chart-trigger:focus-visible {
  outline: 2px solid var(--color-text-primary, #151515);
  outline-offset: 3px;
}

.size-chart-trigger__icon {
  flex-shrink: 0;
  color: var(--color-text-muted, #9a9a9a);
  transition: color var(--transition-fast, 150ms ease);
}

.size-chart-trigger:hover .size-chart-trigger__icon,
.size-chart-trigger:focus-visible .size-chart-trigger__icon {
  color: currentColor;
}

.size-chart-trigger span {
  border-bottom: 1px solid currentColor;
  line-height: 1.1;
}

.size-chart-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(21, 21, 21, 0.55);
  display: grid;
  place-items: end center;
  padding: 0;
  z-index: var(--z-modal, 400);
}

@media (min-width: 640px) {
  .size-chart-backdrop {
    place-items: center;
    padding: 32px 24px;
  }
}

.size-chart-dialog {
  width: 100%;
  max-width: 760px;
  max-height: 92vh;
  background: var(--color-white, #fff);
  border-radius: var(--radius-lg, 8px) var(--radius-lg, 8px) 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: var(--shadow-xl, 0 16px 40px rgba(21, 21, 21, 0.16));
}

@media (min-width: 640px) {
  .size-chart-dialog {
    border-radius: var(--radius-lg, 8px);
    max-height: 86vh;
  }
}

.size-chart-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 24px 20px;
  border-bottom: 1px solid var(--color-border, #e5e5e5);
}

.size-chart-dialog__eyebrow {
  margin: 0 0 4px;
  font-size: 0.7rem;
  letter-spacing: var(--tracking-wider, 0.1em);
  text-transform: uppercase;
  color: var(--color-text-muted, #9a9a9a);
}

.size-chart-dialog__title {
  margin: 0;
  font-family: var(--font-serif, Georgia, serif);
  font-size: clamp(1.5rem, 1.2rem + 1vw, 2rem);
  font-weight: var(--font-medium, 500);
  letter-spacing: var(--tracking-tight, -0.02em);
  line-height: 1.15;
  color: var(--color-text-primary, #151515);
}

.size-chart-dialog__hint {
  margin: 10px 0 0;
  font-size: 0.85rem;
  color: var(--color-text-secondary, #5a5a5a);
}

.size-chart-dialog__hint-value {
  display: inline-block;
  margin-left: 4px;
  padding: 2px 8px;
  background: var(--color-text-primary, #151515);
  color: var(--color-text-inverse, #fff);
  border-radius: var(--radius-sm, 2px);
  font-weight: var(--font-semibold, 600);
  font-size: 0.8rem;
  letter-spacing: var(--tracking-wide, 0.05em);
}

.size-chart-dialog__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  background: var(--color-surface, #f2f2f2);
  border: 0;
  border-radius: var(--radius-full, 9999px);
  color: var(--color-text-primary, #151515);
  cursor: pointer;
  transition: background var(--transition-fast, 150ms ease);
}

.size-chart-dialog__close:hover,
.size-chart-dialog__close:focus-visible {
  background: var(--color-border, #e5e5e5);
  outline: none;
}

.size-chart-dialog__body {
  padding: 20px 24px 28px;
  overflow-y: auto;
}

.size-chart-table-wrap {
  margin: 0 -8px;
  padding: 0 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.size-chart-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.875rem;
  color: var(--color-text-primary, #151515);
}

.size-chart-table thead th {
  padding: 12px 10px;
  font-weight: var(--font-semibold, 600);
  text-align: center;
  background: var(--color-surface, #f2f2f2);
  border-bottom: 1px solid var(--color-border, #e5e5e5);
  color: var(--color-text-primary, #151515);
  white-space: nowrap;
  position: sticky;
  top: 0;
}

.size-chart-table thead th:first-child {
  text-align: left;
  position: sticky;
  left: 0;
  z-index: 2;
}

.size-chart-table__th--selected {
  background: var(--color-text-primary, #151515) !important;
  color: var(--color-text-inverse, #fff) !important;
}

.size-chart-table tbody td,
.size-chart-table tbody th {
  padding: 12px 10px;
  text-align: center;
  border-bottom: 1px solid var(--color-border, #e5e5e5);
  white-space: nowrap;
}

.size-chart-table__label {
  text-align: left !important;
  font-weight: var(--font-medium, 500);
  color: var(--color-text-secondary, #5a5a5a);
  background: var(--color-white, #fff);
  position: sticky;
  left: 0;
  z-index: 1;
}

.size-chart-table tbody tr:last-child td,
.size-chart-table tbody tr:last-child th {
  border-bottom: 0;
}

.size-chart-table__cell--selected {
  background: var(--color-surface, #f2f2f2);
  color: var(--color-text-primary, #151515);
  font-weight: var(--font-semibold, 600);
  position: relative;
}

.size-chart-table__cell--selected::before {
  content: '';
  position: absolute;
  inset: 4px;
  border: 1.5px solid var(--color-text-primary, #151515);
  border-radius: var(--radius-sm, 2px);
  pointer-events: none;
}

.size-chart-table__tr--size-row .size-chart-table__label {
  color: var(--color-text-primary, #151515);
  font-weight: var(--font-semibold, 600);
}

.size-chart-dialog__note {
  margin: 16px 0 0;
  font-size: 0.8rem;
  color: var(--color-text-muted, #9a9a9a);
  line-height: 1.6;
}

.size-chart-html :deep(table) {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.size-chart-html :deep(th),
.size-chart-html :deep(td) {
  padding: 8px 12px;
  border: 1px solid var(--color-border, #e5e5e5);
}

.size-chart-modal-enter-active,
.size-chart-modal-leave-active {
  transition: opacity var(--transition-base, 250ms ease);
}

.size-chart-modal-enter-active .size-chart-dialog,
.size-chart-modal-leave-active .size-chart-dialog {
  transition: transform var(--transition-base, 250ms ease);
}

.size-chart-modal-enter-from,
.size-chart-modal-leave-to {
  opacity: 0;
}

.size-chart-modal-enter-from .size-chart-dialog,
.size-chart-modal-leave-to .size-chart-dialog {
  transform: translateY(20px);
}
</style>
